// src/pages/medichat/SettingsPage.jsx
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { axiosInstance } from '../../utils/axios';
import PageHeader from './PageHeader';
import { FaCheckCircle } from 'react-icons/fa';
import AlertModal from '../common/AlertModal';
import FormField from '../common/FormField';
import { useFormValidation } from '../../utils/useFormValidation';
import { hiraganaToKatakana } from '../../utils/kanaConverter';
import MypageTargetSwitcher from './MypageTargetSwitcher';
import ChildProfileModal from './ChildProfileModal';
import { Plus } from 'lucide-react';
import './medichat_mypage.scss';
import '../common/form.scss';

/* ① 都道府県の定義（空配列をやめて確実に選択肢を出す） */
const PREFS = [
  '北海道','青森県','岩手県','宮城県','秋田県','山形県','福島県',
  '茨城県','栃木県','群馬県','埼玉県','千葉県','東京都','神奈川県',
  '新潟県','富山県','石川県','福井県','山梨県','長野県',
  '岐阜県','静岡県','愛知県','三重県',
  '滋賀県','京都府','大阪府','兵庫県','奈良県','和歌山県',
  '鳥取県','島根県','岡山県','広島県','山口県',
  '徳島県','香川県','愛媛県','高知県',
  '福岡県','佐賀県','長崎県','熊本県','大分県','宮崎県','鹿児島県','沖縄県',
];

/* ② プロフィール値→選択肢名への正規化 */

const isBlank = (v) => v === undefined || v === null || String(v).trim() === '';
const childGenderLabel = (gender) => (
  gender === 'male' ? '男の子' : gender === 'female' ? '女の子' : gender === 'other' ? 'その他' : '未設定'
);

const CHILD_ALLERGY_OPTIONS = [
  { value: 'wheat', label: '小麦', key: 'allergy_wheat' },
  { value: 'egg', label: '卵', key: 'allergy_egg' },
  { value: 'milk', label: '乳', key: 'allergy_milk' },
  { value: 'shrimp', label: 'えび', key: 'allergy_shrimp' },
  { value: 'crab', label: 'かに', key: 'allergy_crab' },
  { value: 'buckwheat', label: 'そば', key: 'allergy_buckwheat' },
  { value: 'peanut', label: '落花生', key: 'allergy_peanut' },
  { value: 'other', label: 'その他', key: 'allergy_other' },
];

const CHILD_HISTORY_OPTIONS = [
  { value: 'pneumonia', label: '肺炎', key: 'history_pneumonia' },
  { value: 'heart_disease', label: '心臓病', key: 'history_heart_disease' },
  { value: 'convulsion', label: 'けいれん', key: 'history_convulsion' },
  { value: 'atopic_dermatitis', label: 'アトピー性皮膚炎', key: 'history_atopic_dermatitis' },
  { value: 'congenital_disease', label: '先天性疾患', key: 'history_congenital_disease' },
  { value: 'urinary_tract_infection', label: '尿路感染症', key: 'history_urinary_tract_infection' },
  { value: 'kawasaki_disease', label: '川崎病', key: 'history_kawasaki_disease' },
  { value: 'developmental_disorder', label: '発達障害', key: 'history_developmental_disorder' },
  { value: 'asthma', label: '喘息', key: 'history_asthma' },
  { value: 'hospitalization_surgery', label: '入院・手術', key: 'history_hospitalization_surgery' },
  { value: 'other', label: 'その他', key: 'history_other' },
];

function SettingsChip({ label, checked, onChange }) {
  return (
    <button type="button" onClick={onChange} style={{
      padding: '6px 12px', border: `1.5px solid ${checked ? '#4f46e5' : '#e5e7eb'}`,
      borderRadius: '20px', background: checked ? '#eff6ff' : '#f9fafb',
      color: checked ? '#4f46e5' : '#374151', fontSize: '13px',
      fontWeight: checked ? '600' : '400', cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
    }}>{label}</button>
  );
}

const SettingsPage = () => {
  const [profile, setProfile] = useState(null);
  const [childProfiles, setChildProfiles] = useState([]);
  const [selectedTargetId, setSelectedTargetId] = useState('self');
  const [editingChild, setEditingChild] = useState(null);
  const [childModalOpen, setChildModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '' });
  // 子供の編集用ローカルステート
  const [childForm, setChildForm] = useState(null);
  // フリガナ入力のIME変換中フラグ
  const isComposingKanaRef = useRef(false);

  // バリデーション
  const { errors, validateForm, getFieldError, clearErrors } = useFormValidation('user');

  useEffect(() => {
    // ページ表示時に画面上部にスクロール
    window.scrollTo(0, 0);
    
    (async () => {
      try {
        const [profileRes, childRes] = await Promise.all([
          axiosInstance.get('/api/medichat/profile'),
          axiosInstance.get('/api/medichat/child_profiles').catch(() => ({ data: { child_profiles: [] } })),
        ]);
        setProfile(profileRes?.data?.user || {});
        setChildProfiles(childRes?.data?.child_profiles || []);
      } catch {
        setProfile({});
        setChildProfiles([]);
      }
    })();
  }, []);

  const reloadChildren = async () => {
    const { data } = await axiosInstance.get('/api/medichat/child_profiles').catch(() => ({ data: { child_profiles: [] } }));
    setChildProfiles(data?.child_profiles || []);
  };

  const targets = useMemo(() => ([
    { id: 'self', label: '自分' },
    ...childProfiles.map((child) => ({ id: `child-${child.id}`, label: child.full_name || `${child.last_name || ''} ${child.first_name || ''}`.trim() })),
    { id: 'add-child', label: '＋ お子さまを追加', isAction: true },
  ]), [childProfiles]);

  const selectedChild = useMemo(() => {
    if (!selectedTargetId.startsWith('child-')) return null;
    const childId = Number(selectedTargetId.replace('child-', ''));
    return childProfiles.find((child) => child.id === childId) || null;
  }, [selectedTargetId, childProfiles]);

  useEffect(() => {
    if (selectedChild) {
      const allergies = [];
      if (!selectedChild.has_allergy) {
        allergies.push('none');
      } else {
        CHILD_ALLERGY_OPTIONS.forEach(o => {
          if (o.value !== 'other' && selectedChild[o.key]) allergies.push(o.value);
        });
        if (selectedChild.allergy_other) allergies.push('other');
      }
      const histories = [];
      if (!selectedChild.has_medical_history) {
        histories.push('none');
      } else {
        CHILD_HISTORY_OPTIONS.forEach(o => {
          if (o.value !== 'other' && selectedChild[o.key]) histories.push(o.value);
        });
        if (selectedChild.history_other) histories.push('other');
      }
      setChildForm({
        ...selectedChild,
        _selectedAllergies: allergies,
        _allergyOther: selectedChild.allergy_other || '',
        _selectedHistory: histories,
        _historyOther: selectedChild.history_other || '',
      });
    } else {
      setChildForm(null);
    }
  }, [selectedChild]);

  const hasAnyRequiredEmpty = useMemo(() => {
    if (!profile) return true;
    const req = [
      'gender','prefecture','birthdate',
      'phone','email',
      'height','weight'
    ];
    return req.some((k) => isBlank(profile[k]));
  }, [profile]);

  const saveChildProfile = async () => {
    if (!childForm || !selectedChild) return;
    setSaving(true);
    const sa = childForm._selectedAllergies || [];
    const sh = childForm._selectedHistory || [];
    try {
      await axiosInstance.put(`/api/medichat/child_profiles/${selectedChild.id}`, {
        child_profile: {
          last_name: childForm.last_name || '',
          first_name: childForm.first_name || '',
          last_name_kana: childForm.last_name_kana || '',
          first_name_kana: childForm.first_name_kana || '',
          gender: childForm.gender || '',
          birth_date: childForm.birth_date || '',
          has_allergy: !sa.includes('none') && sa.length > 0,
          allergy_wheat: sa.includes('wheat'),
          allergy_egg: sa.includes('egg'),
          allergy_milk: sa.includes('milk'),
          allergy_shrimp: sa.includes('shrimp'),
          allergy_crab: sa.includes('crab'),
          allergy_buckwheat: sa.includes('buckwheat'),
          allergy_peanut: sa.includes('peanut'),
          allergy_other: sa.includes('other') ? (childForm._allergyOther?.trim() || null) : null,
          has_medical_history: sh.filter(v => v !== 'none').length > 0,
          history_pneumonia: sh.includes('pneumonia'),
          history_heart_disease: sh.includes('heart_disease'),
          history_convulsion: sh.includes('convulsion'),
          history_atopic_dermatitis: sh.includes('atopic_dermatitis'),
          history_congenital_disease: sh.includes('congenital_disease'),
          history_urinary_tract_infection: sh.includes('urinary_tract_infection'),
          history_kawasaki_disease: sh.includes('kawasaki_disease'),
          history_developmental_disorder: sh.includes('developmental_disorder'),
          history_asthma: sh.includes('asthma'),
          history_hospitalization_surgery: sh.includes('hospitalization_surgery'),
          history_other: sh.includes('other') ? (childForm._historyOther?.trim() || null) : null,
        }
      });
      await reloadChildren();
      setDoneOpen(true);
    } catch (e) {
      setAlertModal({ open: true, title: 'エラー', message: e?.response?.data?.error || '保存に失敗しました' });
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = useMemo(() => async () => {
    if (!profile || hasAnyRequiredEmpty) return;
    
    // バリデーション実行
    const formData = {
      last_name: profile.last_name,
      first_name: profile.first_name,
      email_user: profile.email,
      phone_number: profile.phone,
      birth_date: profile.birthdate,
      gender: profile.gender,
      prefecture: profile.prefecture,
      height: profile.height,
      weight: profile.weight
    };

    const requiredFieldsForSettings = [
      'email_user',
      'phone_number',
      'birth_date',
      'gender',
      'prefecture',
      'height',
      'weight'
    ];

    if (!validateForm(formData, requiredFieldsForSettings)) {
      const errorMessages = Object.values(errors)
        .filter(msg => msg)
        .join('\n');
      setAlertModal({ 
        open: true, 
        title: '入力エラー', 
        message: errorMessages || '入力内容を確認してください' 
      });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        user: {
          last_name:        profile.last_name || '',
          first_name:       profile.first_name || '',
          last_name_kana:   profile.last_name_kana || '',
          first_name_kana:  profile.first_name_kana || '',
          birthdate:        profile.birthdate || '',
          gender:           profile.gender || '',
          phone:            profile.phone || '',
          email:            profile.email || '',
          prefecture:       profile.prefecture || '',
          height:           profile.height || '',
          weight:           profile.weight || '',
          allergies:        profile.allergies || '',
          allergy_details:  profile.allergy_details || '',
          medical_history:  profile.medical_history || '',
          current_medications: profile.current_medications || '',
          family_doctor_clinic: profile.family_doctor_clinic || '',
          pregnancy_status: profile.pregnancy_status || '',
          smoking_habit: profile.smoking_habit || '',
          smoking_cigarettes_per_day: profile.smoking_cigarettes_per_day || '',
          drinking_habit: profile.drinking_habit || ''
        }
      };
      await axiosInstance.put('/api/medichat/profile', payload);
      clearErrors();
      setDoneOpen(true); // ← 保存成功でモーダル
    } catch (e) {
      const errorMessage = e?.response?.data?.error || 
                          e?.response?.data?.message || 
                          e?.message || 
                          '保存に失敗しました';
      setAlertModal({ open: true, title: 'エラー', message: errorMessage });
    } finally {
      setSaving(false);
    }
  }, [profile, hasAnyRequiredEmpty, validateForm, errors, clearErrors]);

  return (
    <div className="mypage-container mypage-container--with-fixed-footer">
      <PageHeader title="基本情報" />

      <div className="mypage-section" style={{marginTop:'0px',boxShadow:'none'}}>
        <MypageTargetSwitcher
          targets={targets}
          currentTargetId={selectedTargetId}
          onChange={(id) => {
            if (id === 'add-child') {
              setEditingChild(null);
              setChildModalOpen(true);
            } else {
              setSelectedTargetId(id);
            }
          }}
        />

        {selectedTargetId === 'self' ? (
        <div className="mypage-grid">
          {/* 姓・名（表示のみ・編集不可） */}
          <div style={{display:'flex',justifyContent:'space-between',width:'100%',gap:'8px',boxSizing:'border-box'}}>
            <div style={{width:'49%',boxSizing:'border-box'}}>
              <div style={{marginBottom:'16px'}}>
                <label style={{display:'block',fontSize:'13px',fontWeight:'600',color:'#374151',marginBottom:'6px'}}>姓</label>
                <div style={{padding:'10px 12px',background:'#f3f4f6',borderRadius:'8px',fontSize:'15px',color:'#374151',minHeight:'20px'}}>
                  {profile?.last_name || '未設定'}
                </div>
              </div>
            </div>
            <div style={{width:'49%',boxSizing:'border-box'}}>
              <div style={{marginBottom:'16px'}}>
                <label style={{display:'block',fontSize:'13px',fontWeight:'600',color:'#374151',marginBottom:'6px'}}>名</label>
                <div style={{padding:'10px 12px',background:'#f3f4f6',borderRadius:'8px',fontSize:'15px',color:'#374151',minHeight:'20px'}}>
                  {profile?.first_name || '未設定'}
                </div>
              </div>
            </div>
          </div>

          {/* 姓・名（フリガナ）（表示のみ・編集不可） */}
          <div style={{display:'flex',justifyContent:'space-between',width:'100%',gap:'8px',boxSizing:'border-box'}}>
            <div style={{width:'49%',boxSizing:'border-box'}}>
              <div style={{marginBottom:'16px'}}>
                <label style={{display:'block',fontSize:'13px',fontWeight:'600',color:'#374151',marginBottom:'6px'}}>姓（フリガナ）</label>
                <div style={{padding:'10px 12px',background:'#f3f4f6',borderRadius:'8px',fontSize:'15px',color:'#374151',minHeight:'20px'}}>
                  {profile?.last_name_kana || '未設定'}
                </div>
              </div>
            </div>
            <div style={{width:'49%',boxSizing:'border-box'}}>
              <div style={{marginBottom:'16px'}}>
                <label style={{display:'block',fontSize:'13px',fontWeight:'600',color:'#374151',marginBottom:'6px'}}>名（フリガナ）</label>
                <div style={{padding:'10px 12px',background:'#f3f4f6',borderRadius:'8px',fontSize:'15px',color:'#374151',minHeight:'20px'}}>
                  {profile?.first_name_kana || '未設定'}
                </div>
              </div>
            </div>
          </div>

          {/* 性別・都道府県 */}
          <div style={{display:'flex',justifyContent:'space-between',width:'100%',gap:'8px',boxSizing:'border-box'}}>
            <div style={{width:'49%',boxSizing:'border-box'}}>
              <FormField
                label="性別"
                name="gender"
                as="select"
                value={profile?.gender||''}
                onChange={(name, value) => setProfile(p=>({...p, gender: value}))}
                required={true}
              >
                <option value="">選択してください</option>
                <option value="男性">男性</option>
                <option value="女性">女性</option>
              </FormField>
            </div>

            <div style={{width:'49%',boxSizing:'border-box'}}>
              <FormField
                label="都道府県"
                name="prefecture"
                as="select"
                value={profile?.prefecture||''}
                onChange={(name, value) => setProfile(p=>({...p, prefecture: value}))}
                required={true}
              >
                <option value="">選択してください</option>
                {PREFS.map(pref => (
                  <option key={pref} value={pref}>{pref}</option>
                ))}
              </FormField>
            </div>
          </div>

          {/* 生年月日 */}
          <div className="mc-form-field">
            <label className="mc-form-field__label">
              生年月日
              <span className="mc-form-field__required">※必須</span>
            </label>
            <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
              <select
                className="mc-form-field__input"
                value={(() => {
                  if (!profile?.birthdate) return '';
                  const parts = profile.birthdate.split('-');
                  return parts[0] || '';
                })()}
                onChange={(e) => {
                  const year = e.target.value;
                  const parts = (profile?.birthdate || '--').split('-');
                  const month = parts[1] || '';
                  const day = parts[2] || '';
                  const newDate = `${year}-${month}-${day}`;
                  setProfile(p => ({ ...p, birthdate: newDate }));
                }}
                style={{ flex: 1 }}
              >
                <option value="">年</option>
                {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={y}>{y}年</option>
                ))}
              </select>
              <select
                className="mc-form-field__input"
                value={(() => {
                  if (!profile?.birthdate) return '';
                  const parts = profile.birthdate.split('-');
                  return parts[1] || '';
                })()}
                onChange={(e) => {
                  const month = e.target.value.padStart(2, '0');
                  const parts = (profile?.birthdate || '--').split('-');
                  const year = parts[0] || '';
                  const day = parts[2] || '';
                  const newDate = `${year}-${month}-${day}`;
                  setProfile(p => ({ ...p, birthdate: newDate }));
                }}
                style={{ flex: 1 }}
              >
                <option value="">月</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                  <option key={m} value={String(m).padStart(2, '0')}>{m}月</option>
                ))}
              </select>
              <select
                className="mc-form-field__input"
                value={(() => {
                  if (!profile?.birthdate) return '';
                  const parts = profile.birthdate.split('-');
                  return parts[2] || '';
                })()}
                onChange={(e) => {
                  const day = e.target.value.padStart(2, '0');
                  const parts = (profile?.birthdate || '--').split('-');
                  const year = parts[0] || '';
                  const month = parts[1] || '';
                  const newDate = `${year}-${month}-${day}`;
                  setProfile(p => ({ ...p, birthdate: newDate }));
                }}
                style={{ flex: 1 }}
              >
                <option value="">日</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={String(d).padStart(2, '0')}>{d}日</option>
                ))}
              </select>
            </div>
          </div>

          {/* 電話番号 */}
          <FormField
            label="電話番号"
            name="phone"
            type="tel"
            value={profile?.phone||''}
            onChange={(name, value) => setProfile(p=>({...p, phone: value}))}
            required={true}
            placeholder="09012345678"
            helperText="ハイフンなしで入力してください"
          />

          {/* メールアドレス */}
          <FormField
            label="メールアドレス"
            name="email"
            type="email"
            value={profile?.email||''}
            onChange={(name, value) => setProfile(p=>({...p, email: value}))}
            required={true}
            placeholder="example@email.com"
          />

          {/* 身長・体重 */}
          <div style={{display:'flex',justifyContent:'space-between',width:'100%',gap:'8px',boxSizing:'border-box'}}>
            <div style={{width:'49%',boxSizing:'border-box'}}>
              <FormField
                label="身長(cm)"
                name="height"
                type="number"
                value={profile?.height||''}
                onChange={(name, value) => setProfile(p=>({...p, height: value}))}
                required={true}
                placeholder="170"
              />
            </div>

            <div style={{width:'49%',boxSizing:'border-box'}}>
              <FormField
                label="体重(kg)"
                name="weight"
                type="number"
                value={profile?.weight||''}
                onChange={(name, value) => setProfile(p=>({...p, weight: value}))}
                required={true}
                placeholder="65"
              />
            </div>
          </div>

          {/* アレルギー */}
          <FormField
            label="アレルギー有無・詳細"
            name="allergies"
            as="textarea"
            value={profile?.allergies||''}
            onChange={(name, value) => setProfile(p=>({...p, allergies: value}))}
            required={false}
            optional={true}
            placeholder="例：なし / 小麦アレルギー / 猫アレルギー、花粉症"
            rows={2}
          />

          {/* 既往歴 */}
          <FormField
            label="既往歴"
            name="medical_history"
            as="textarea"
            value={profile?.medical_history||''}
            onChange={(name, value) => setProfile(p=>({...p, medical_history: value}))}
            required={false}
            optional={true}
            placeholder="これまでにかかった病気や怪我があれば記入してください"
            rows={3}
          />

          {/* 現在の服薬内容 */}
          <FormField
            label="現在の服薬内容"
            name="current_medications"
            as="textarea"
            value={profile?.current_medications||''}
            onChange={(name, value) => setProfile(p=>({...p, current_medications: value}))}
            required={false}
            optional={true}
            placeholder="現在服用しているお薬があれば記入してください。ない場合は「なし」と記入"
            rows={3}
          />

          <FormField
            label="かかりつけ医療機関/希望紹介先"
            name="family_doctor_clinic"
            value={profile?.family_doctor_clinic||''}
            onChange={(name, value) => setProfile(p=>({...p, family_doctor_clinic: value}))}
            required={false}
            optional={true}
            placeholder="例：〇〇病院 / △△クリニック"
            helperText="希望紹介先は参考情報です。実際の紹介先は診察内容を踏まえて担当医と相談の上で決定される場合があります。"
          />

          {/* 妊娠/授乳状況（女性のみ） */}
          {profile?.gender === '女性' && (
            <FormField
              label="妊娠 / 授乳状況"
              name="pregnancy_status"
              as="textarea"
              value={profile?.pregnancy_status||''}
              onChange={(name, value) => setProfile(p=>({...p, pregnancy_status: value}))}
              required={false}
              optional={true}
              placeholder="例：いいえ / 妊娠中（〇週目） / 授乳中"
              rows={2}
            />
          )}

          <FormField
            label="喫煙習慣"
            name="smoking_habit"
            as="select"
            value={profile?.smoking_habit || ''}
            onChange={(name, value) => setProfile(p => ({
              ...p,
              smoking_habit: value,
              smoking_cigarettes_per_day: value === 'はい' ? (p?.smoking_cigarettes_per_day || '') : ''
            }))}
            required={false}
            optional={true}
          >
            <option value="">選択してください</option>
            <option value="はい">はい</option>
            <option value="いいえ">いいえ</option>
          </FormField>

          {profile?.smoking_habit === 'はい' && (
            <FormField
              label="1日の喫煙本数"
              name="smoking_cigarettes_per_day"
              value={profile?.smoking_cigarettes_per_day || ''}
              onChange={(name, value) => setProfile(p => ({ ...p, smoking_cigarettes_per_day: value }))}
              required={false}
              optional={true}
              placeholder="例：10本"
            />
          )}

          <FormField
            label="飲酒習慣"
            name="drinking_habit"
            as="select"
            value={profile?.drinking_habit || ''}
            onChange={(name, value) => setProfile(p => ({ ...p, drinking_habit: value }))}
            required={false}
            optional={true}
          >
            <option value="">選択してください</option>
            <option value="ない">ない</option>
            <option value="週に1回~2回">週に1回~2回</option>
            <option value="週に2回~3回">週に2回~3回</option>
            <option value="週に3~4回">週に3~4回</option>
            <option value="週に5回以上">週に5回以上</option>
          </FormField>
        </div>
        ) : selectedChild && childForm ? (
          <div className="mypage-grid">
            {/* 姓・名（表示のみ・編集不可） */}
            <div style={{display:'flex',justifyContent:'space-between',width:'100%',gap:'8px',boxSizing:'border-box'}}>
              <div style={{width:'49%',boxSizing:'border-box'}}>
                <div style={{marginBottom:'16px'}}>
                  <label style={{display:'block',fontSize:'13px',fontWeight:'600',color:'#374151',marginBottom:'6px'}}>姓</label>
                  <div style={{padding:'10px 12px',background:'#f3f4f6',borderRadius:'8px',fontSize:'15px',color:'#374151',minHeight:'20px'}}>
                    {childForm.last_name || '未設定'}
                  </div>
                </div>
              </div>
              <div style={{width:'49%',boxSizing:'border-box'}}>
                <div style={{marginBottom:'16px'}}>
                  <label style={{display:'block',fontSize:'13px',fontWeight:'600',color:'#374151',marginBottom:'6px'}}>名</label>
                  <div style={{padding:'10px 12px',background:'#f3f4f6',borderRadius:'8px',fontSize:'15px',color:'#374151',minHeight:'20px'}}>
                    {childForm.first_name || '未設定'}
                  </div>
                </div>
              </div>
            </div>

            {/* 姓・名（フリガナ）（表示のみ・編集不可） */}
            <div style={{display:'flex',justifyContent:'space-between',width:'100%',gap:'8px',boxSizing:'border-box'}}>
              <div style={{width:'49%',boxSizing:'border-box'}}>
                <div style={{marginBottom:'16px'}}>
                  <label style={{display:'block',fontSize:'13px',fontWeight:'600',color:'#374151',marginBottom:'6px'}}>姓（フリガナ）</label>
                  <div style={{padding:'10px 12px',background:'#f3f4f6',borderRadius:'8px',fontSize:'15px',color:'#374151',minHeight:'20px'}}>
                    {childForm.last_name_kana || '未設定'}
                  </div>
                </div>
              </div>
              <div style={{width:'49%',boxSizing:'border-box'}}>
                <div style={{marginBottom:'16px'}}>
                  <label style={{display:'block',fontSize:'13px',fontWeight:'600',color:'#374151',marginBottom:'6px'}}>名（フリガナ）</label>
                  <div style={{padding:'10px 12px',background:'#f3f4f6',borderRadius:'8px',fontSize:'15px',color:'#374151',minHeight:'20px'}}>
                    {childForm.first_name_kana || '未設定'}
                  </div>
                </div>
              </div>
            </div>

            {/* 性別 */}
            <FormField
              label="性別"
              name="child_gender"
              as="select"
              value={childForm.gender||''}
              onChange={(name, value) => setChildForm(f=>({...f, gender: value}))}
              required={true}
            >
              <option value="">選択してください</option>
              <option value="male">男の子</option>
              <option value="female">女の子</option>
            </FormField>

            {/* 生年月日 */}
            <div className="mc-form-field">
              <label className="mc-form-field__label">
                生年月日
                <span className="mc-form-field__required">※必須</span>
              </label>
              <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                <select
                  className="mc-form-field__input"
                  value={(() => {
                    if (!childForm.birth_date) return '';
                    const parts = childForm.birth_date.split('-');
                    return parts[0] || '';
                  })()}
                  onChange={(e) => {
                    const year = e.target.value;
                    const parts = (childForm.birth_date || '--').split('-');
                    const month = parts[1] || '';
                    const day = parts[2] || '';
                    setChildForm(f => ({ ...f, birth_date: `${year}-${month}-${day}` }));
                  }}
                  style={{ flex: 1 }}
                >
                  <option value="">年</option>
                  {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={y}>{y}年</option>
                  ))}
                </select>
                <select
                  className="mc-form-field__input"
                  value={(() => {
                    if (!childForm.birth_date) return '';
                    const parts = childForm.birth_date.split('-');
                    return parts[1] || '';
                  })()}
                  onChange={(e) => {
                    const month = e.target.value.padStart(2, '0');
                    const parts = (childForm.birth_date || '--').split('-');
                    const year = parts[0] || '';
                    const day = parts[2] || '';
                    setChildForm(f => ({ ...f, birth_date: `${year}-${month}-${day}` }));
                  }}
                  style={{ flex: 1 }}
                >
                  <option value="">月</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={String(m).padStart(2, '0')}>{m}月</option>
                  ))}
                </select>
                <select
                  className="mc-form-field__input"
                  value={(() => {
                    if (!childForm.birth_date) return '';
                    const parts = childForm.birth_date.split('-');
                    return parts[2] || '';
                  })()}
                  onChange={(e) => {
                    const day = e.target.value.padStart(2, '0');
                    const parts = (childForm.birth_date || '--').split('-');
                    const year = parts[0] || '';
                    const month = parts[1] || '';
                    setChildForm(f => ({ ...f, birth_date: `${year}-${month}-${day}` }));
                  }}
                  style={{ flex: 1 }}
                >
                  <option value="">日</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <option key={d} value={String(d).padStart(2, '0')}>{d}日</option>
                  ))}
                </select>
              </div>
            </div>

            {/* アレルギー */}
            <div className="mc-form-field">
              <label className="mc-form-field__label">アレルギー <span className="mc-form-field__required">※必須</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' }}>
                <SettingsChip label="なし（該当なし）" checked={(childForm._selectedAllergies || []).includes('none')}
                  onChange={() => setChildForm(f => ({
                    ...f,
                    _selectedAllergies: (f._selectedAllergies || []).includes('none') ? [] : ['none']
                  }))} />
                {CHILD_ALLERGY_OPTIONS.map(o => (
                  <SettingsChip key={o.value} label={o.label}
                    checked={(childForm._selectedAllergies || []).includes(o.value)}
                    onChange={() => setChildForm(f => {
                      const without = (f._selectedAllergies || []).filter(v => v !== 'none');
                      return { ...f, _selectedAllergies: without.includes(o.value) ? without.filter(v => v !== o.value) : [...without, o.value] };
                    })} />
                ))}
              </div>
              {(childForm._selectedAllergies || []).includes('other') && (
                <input className="mc-form-field__input" type="text" placeholder="アレルギーの内容を入力"
                  value={childForm._allergyOther || ''} onChange={e => setChildForm(f => ({ ...f, _allergyOther: e.target.value }))}
                  style={{ marginTop: '8px' }} />
              )}
            </div>

            {/* 既往歴・持病 */}
            <div className="mc-form-field">
              <label className="mc-form-field__label">既往歴・持病 <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '400' }}>（任意）</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                <SettingsChip label="なし（該当なし）" checked={(childForm._selectedHistory || []).includes('none')}
                  onChange={() => setChildForm(f => ({
                    ...f,
                    _selectedHistory: (f._selectedHistory || []).includes('none') ? [] : ['none']
                  }))} />
                {CHILD_HISTORY_OPTIONS.map(o => (
                  <SettingsChip key={o.value} label={o.label}
                    checked={(childForm._selectedHistory || []).includes(o.value)}
                    onChange={() => setChildForm(f => {
                      const without = (f._selectedHistory || []).filter(v => v !== 'none');
                      return { ...f, _selectedHistory: without.includes(o.value) ? without.filter(v => v !== o.value) : [...without, o.value] };
                    })} />
                ))}
              </div>
              {(childForm._selectedHistory || []).includes('other') && (
                <input className="mc-form-field__input" type="text" placeholder="既往歴・持病の内容を入力"
                  value={childForm._historyOther || ''} onChange={e => setChildForm(f => ({ ...f, _historyOther: e.target.value }))}
                  style={{ marginTop: '8px' }} />
              )}
            </div>
          </div>
        ) : null}

        <div className="mc-fixed-footer-spacer" />
      </div>

      <div className="mc-fixed-footer">
        {selectedTargetId === 'self' ? (
          <button
            className={`mc-mypage-ins-submitbtn mc-fixed-footer__btn ${hasAnyRequiredEmpty ? 'is-disabled' : ''}`}
            onClick={saveProfile}
            disabled={saving || hasAnyRequiredEmpty}
            aria-disabled={saving || hasAnyRequiredEmpty}
            title={hasAnyRequiredEmpty ? '未入力の必須項目があります' : undefined}
          >
            {saving ? '保存中…' : '保存'}
          </button>
        ) : selectedChild && childForm ? (
          <button
            className="mc-mypage-ins-submitbtn mc-fixed-footer__btn"
            onClick={saveChildProfile}
            disabled={saving}
          >
            {saving ? '保存中…' : '保存'}
          </button>
        ) : null}
      </div>

      {/* 成功モーダル（プロフィールのモーダルUIを流用） */}
      {doneOpen && (
        <div className="modal-overlay" onClick={() => setDoneOpen(false)}>
          <div className="modal-content success_modal" onClick={(e) => e.stopPropagation()}>
            <span className="close-button" onClick={() => setDoneOpen(false)}>✖︎</span>
            <div className="mordal_done">
              <FaCheckCircle className="mordal_done_check" />
              <p className="mordal_done_check_title">基本情報を保存しました</p>
            </div>
          </div>
        </div>
      )}
      
      <AlertModal 
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal({ open: false, title: '', message: '' })}
      />
      <ChildProfileModal
        open={childModalOpen}
        onClose={() => {
          setChildModalOpen(false);
          setEditingChild(null);
        }}
        onSaved={reloadChildren}
        initialProfile={editingChild}
      />
    </div>
  );
};

export default SettingsPage;
